import { useEffect, useRef, useState } from "react";
import { ChatHeader, PresenceAvatar } from "@somethings-ui/web";
import { AlertTriangle, Loader2 } from "lucide-react";
import { DateTime } from "luxon";
import { ChatMessageItem } from "@/components/shared/chat-message-item.tsx";
import type {
  TrainingEvent,
  TrainingAttemptThread,
  TrainingMessage,
} from "@/lib/api/training-simulator/training-simulator.ts";
import { assertNever } from "@/lib/utils.ts";
import { TrainingEscalationFab } from "./training-escalation-fab.tsx";
import { TrainingMessageComposer } from "./training-message-composer.tsx";
import { normalizeTrainingMessage } from "./training-message-adapter.ts";

const TYPING_DELAY_MS = 1500;

interface TrainingSessionChatProps {
  attemptId: string;
  thread: TrainingAttemptThread | undefined;
  messages: TrainingMessage[];
  events?: TrainingEvent[];
  isInProgress: boolean;
  isFinalizing?: boolean;
  replyFailed: boolean;
  readOnly?: boolean;
  onSend?: () => void;
}

export function TrainingSessionChat({
  attemptId,
  thread,
  messages,
  events = [],
  isInProgress,
  isFinalizing = false,
  replyFailed,
  readOnly = false,
  onSend,
}: TrainingSessionChatProps) {
  const teen = thread?.level_teen;
  const transcriptRef = useRef<HTMLDivElement>(null);

  const lastMessage = messages[messages.length - 1];
  const awaitingReply =
    isInProgress &&
    !readOnly &&
    !replyFailed &&
    !isFinalizing &&
    lastMessage?.sender_type === "mentor";

  // Hold the typing indicator back briefly after the mentor sends so the teen
  // doesn't appear to start typing instantly.
  const [showTyping, setShowTyping] = useState(false);

  useEffect(() => {
    if (!awaitingReply) {
      setShowTyping(false);
      return;
    }
    const timer = setTimeout(() => setShowTyping(true), TYPING_DELAY_MS);
    return () => clearTimeout(timer);
  }, [awaitingReply]);

  useEffect(() => {
    const transcript = transcriptRef.current;
    transcript?.scrollTo?.({
      top: transcript.scrollHeight,
      behavior: "smooth",
    });
  }, [messages.length, events.length, showTyping, isFinalizing, replyFailed]);

  const timelineItems = [
    ...messages.map((message, index) => ({
      kind: "message" as const,
      item: message,
      createdAt: message.created_at,
      sequence: index,
    })),
    ...events.map((event, index) => ({
      kind: "event" as const,
      item: event,
      createdAt: event.created_at,
      sequence: messages.length + index,
    })),
  ].sort((a, b) => {
    const byCreatedAt =
      DateTime.fromISO(a.createdAt).toMillis() -
      DateTime.fromISO(b.createdAt).toMillis();

    return byCreatedAt || a.sequence - b.sequence;
  });

  const teenName = teen?.display_name ?? "Simulated teen";
  const subtitle = ["Simulated", teen?.pronouns].filter(
    (part): part is string => !!part,
  );

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-white">
      <ChatHeader>
        <ChatHeader.Leading>
          <PresenceAvatar name={teenName} online size="sm" />
        </ChatHeader.Leading>
        <ChatHeader.Content>
          <ChatHeader.Title>
            <ChatHeader.Name>{teenName}</ChatHeader.Name>
          </ChatHeader.Title>
          <ChatHeader.Subtitle items={subtitle} />
        </ChatHeader.Content>
        {!readOnly ? (
          <TrainingEscalationFab
            attemptId={attemptId}
            threadId={thread?.id}
          />
        ) : null}
      </ChatHeader>

      <div
        ref={transcriptRef}
        className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-white px-6 py-4"
      >
        {timelineItems.map((timelineItem) => {
          if (timelineItem.kind === "event") {
            return (
              <TrainingTimelineEvent
                key={timelineItem.item.id}
                event={timelineItem.item}
              />
            );
          }

          const message = timelineItem.item;

          switch (message.sender_type) {
            case "system":
              return (
                <p
                  key={message.id}
                  className="my-2 text-center text-body-3 text-text-secondary"
                >
                  {message.content}
                </p>
              );
            case "mentor":
              return (
                <ChatMessageItem
                  key={message.id}
                  message={normalizeTrainingMessage(message)}
                  layout={{ alignment: "right", messageStyle: "outgoing" }}
                  sender={{ label: "You" }}
                />
              );
            case "simulated_teen":
              return (
                <ChatMessageItem
                  key={message.id}
                  message={normalizeTrainingMessage(message)}
                  layout={{ alignment: "left", messageStyle: "incoming" }}
                  sender={{ label: teen?.display_name ?? "Simulated teen" }}
                />
              );
            default:
              return assertNever(message.sender_type);
          }
        })}

        {showTyping && <TypingIndicator />}

        {isFinalizing && (
          <div
            role="status"
            className="my-2 flex items-center justify-center gap-2 text-body-3 text-text-secondary"
          >
            <Loader2 className="size-4 animate-spin" />
            Wrapping up your session…
          </div>
        )}

        {replyFailed && (
          <p className="my-2 text-center text-body-3 text-text-secondary">
            {teen?.display_name ?? "The teen"} didn&apos;t reply. Send another
            message to keep going.
          </p>
        )}
      </div>

      {thread && !readOnly && (
        <TrainingMessageComposer
          attemptId={attemptId}
          threadId={thread.id}
          disabled={awaitingReply}
          onSend={onSend}
        />
      )}
      <p className="bg-states-successHighlight py-2 text-center text-body-3 text-states-success">
        Simulation. You&apos;re not messaging a real teen.
      </p>
    </div>
  );
}

function TrainingTimelineEvent({ event }: { event: TrainingEvent }) {
  const formattedTime = DateTime.fromISO(event.created_at).toLocaleString(
    DateTime.TIME_SIMPLE,
  );

  switch (event.event_type) {
    case "simulated_escalation_clicked":
      return (
        <div
          className="my-3 flex justify-center"
          data-testid="training-timeline-event"
        >
          <div className="flex max-w-md items-center gap-2 rounded-full border border-states-error/20 bg-states-errorHighlight px-3 py-1.5 text-body-3 text-states-error">
            <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
            <span>Escalated to the clinical team</span>
            <span className="text-text-secondary">{formattedTime}</span>
          </div>
        </div>
      );
    default:
      return assertNever(event.event_type);
  }
}

function TypingIndicator() {
  return (
    <div
      className="mb-4 flex items-center gap-2"
      data-testid="typing-indicator"
    >
      <div className="flex w-fit items-center gap-1.5 rounded-2xl rounded-bl-sm bg-surfaces-z3 px-4 py-3">
        <span className="size-2 animate-typing-bounce rounded-full bg-text-secondary" />
        <span className="size-2 animate-typing-bounce rounded-full bg-text-secondary [animation-delay:0.2s]" />
        <span className="size-2 animate-typing-bounce rounded-full bg-text-secondary [animation-delay:0.4s]" />
      </div>
    </div>
  );
}
